import dateFormat from "dateformat";
import React, { FC } from "react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Modal } from "@pd/pd-ui-kit";
import { Flex } from "antd";
import { get } from "lodash";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import EnumForm from "~/components/Forms/EnumForm";
import Default from "~/components/Icons/icon-default";
import { STATUS_TYPES } from "~/constans/statuses";
import { useGetLocationData, useSetLocationData } from "~/hooks/locationData";
import {
  Indicator,
  useRestoreIndicatorMutation,
  useSetIndicatorMutation,
} from "~/schemas/generated/graphql";
import { Field } from "~/shared/ui/Field";

import {
  INDICATOR_FORMATS,
  INDICATOR_PERIODS,
  INDICATOR_TYPES,
  INDICATOR_UNITS,
} from "./options";

import s from "./IndicatorForm.less";

const enumFormId = "EnumForm";

interface IProps {
  formId: string;
  setCloseModal: () => void;
  refetch;
  ind?: Indicator;
}

const IndicatorForm: FC<IProps> = ({
  formId,
  setCloseModal,
  refetch,
  ind = {},
}) => {
  const { t } = useTranslation();
  const {
    id,
    format,
    unit = `${INDICATOR_UNITS[0]}`,
    period = `${INDICATOR_PERIODS[1]}`,
    status,
    createdAt,
    type = `${INDICATOR_TYPES[0]}`,
  } = ind;

  const { openChildModal, currentId } = useGetLocationData();
  // Открытие/закрытие дочерней модалки — через replace,
  // чтобы не плодить историю браузера
  const setDataReplace = useSetLocationData(true);

  const schema = yup.object().shape({
    nameRu: yup.string().max(250, t("validation.more250")).nullable(),
    nameEn: yup.string().max(250, t("validation.more250")).nullable(),
    shortNameRu: yup.string().max(250, t("validation.more250")).nullable(),
    shortNameEn: yup.string().max(250, t("validation.more250")).nullable(),
    id: yup
      .number()
      .typeError(t("validation.numeric_field"))
      .integer(t("validation.integer"))
      .min(0, t("validation.negative_area")),
  });

  const { handleSubmit, control, watch, setValue, errors } = useForm({
    defaultValues: {
      ...ind,
      format: format
        ? { value: format, label: t(`indicators.${format}`) }
        : { value: "double", label: t(`indicators.double`) },
      unit: unit
        ? { value: unit, label: t(`indicators.${unit}`) }
        : {
            value: INDICATOR_UNITS[0],
            label: t(`indicators.${INDICATOR_UNITS[0]}`),
          },
      type: type
        ? { value: type, label: t(`indicators.${type}`) }
        : {
            value: INDICATOR_TYPES[0],
            label: t(`indicators.${INDICATOR_TYPES[0]}`),
          },
      period: period
        ? { value: period, label: t(`indicators.${period}`) }
        : {
            value: INDICATOR_PERIODS[1],
            label: t(`indicators.${INDICATOR_PERIODS[1]}`),
          },
      createdAt: dateFormat(createdAt, "dd.mm.yyyy"),
    },
    resolver: yupResolver(schema),
  });
  const isRemoved = status === STATUS_TYPES.REMOVED;
  const isString = get(watch("format"), "value", null) === "string";
  const enums = watch("enum") ? String(watch("enum")).split(",") : null;
  const symbols = enums || ind.enum || [];
  const [setIndicatorMutation] = useSetIndicatorMutation({
    onCompleted() {
      refetch();
      setCloseModal();
    },
  });
  const [restoreIndicatorMutation] = useRestoreIndicatorMutation({
    onCompleted() {
      refetch();
      setCloseModal();
    },
  });
  const onSubmit = (data) => {
    status === STATUS_TYPES.REMOVED
      ? restoreIndicatorMutation({
          variables: {
            id,
          },
        })
      : setIndicatorMutation({
          variables: {
            ...data,
            id: data.id,
            oldId: Object.keys(ind).length > 0 ? id : undefined,
            status: STATUS_TYPES.ACTIVE,
            type: !!data.type.value ? data.type.value : type,
            format: data.format.value,
            unit: !!data.unit.value ? data.unit.value : unit,
            period: !!data.period.value ? data.period.value : period,
            enum: data.format.value === "string" ? symbols : null,
          },
        });
  };

  const openEnumModal = () => {
    setDataReplace("openChildModal", "Enum");
  };

  const handleCloseChildModal = () => {
    setDataReplace("openChildModal", "");
  };

  const selectOptions = (values: string[]) =>
    values.map((value) => ({ value, label: t(`indicators.${value}`) }));

  return (
    <>
      <form
        id={formId}
        className={s.ind_form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Flex vertical gap={10}>
          <Field
            type="input"
            name="id"
            control={control}
            label={t("indicators.indicator_code")}
            error={errors["id"]?.message}
            disabled={isRemoved}
          />
          <Field
            type="input"
            name="enum"
            control={control}
            wrapperClassName={s.no_visible}
          />
          <Field
            type="textarea"
            name="nameRu"
            control={control}
            label={t("indicators.nameRu")}
            error={errors["nameRu"]?.message}
            disabled={isRemoved}
          />
          <Field
            type="textarea"
            name="nameEn"
            control={control}
            label={t("indicators.nameEn")}
            error={errors["nameEn"]?.message}
            disabled={isRemoved}
          />
          <Field
            type="input"
            name="shortNameRu"
            control={control}
            label={t("indicators.shortNameRu")}
            error={errors["shortNameRu"]?.message}
            disabled={isRemoved}
          />
          <Field
            type="input"
            name="shortNameEn"
            control={control}
            label={t("indicators.shortNameEn")}
            error={errors["shortNameEn"]?.message}
            disabled={isRemoved}
          />
          <Field
            type="select"
            name="type"
            control={control}
            label={t("indicators.indicator_type")}
            options={selectOptions(INDICATOR_TYPES)}
            labelInValue
            disabled={isRemoved}
          />
          <Field
            type="select"
            name="format"
            control={control}
            label={t("indicators.format")}
            options={selectOptions(INDICATOR_FORMATS)}
            labelInValue
            disabled={isRemoved}
          />
          {isString && (
            <div className={s.block_dealership}>
              <div className={s.title}>
                {t("indicators.indicator_symbol_title")}
              </div>
              <div className={s.desc}>
                <Button size="small" icon={<Default />} onClick={openEnumModal}>
                  {t("ui.btn_edit")}
                </Button>
                <div className={s.wrap_dealers}>
                  {symbols.map((el, i) => (
                    <div key={i} className={s.dealer_name}>
                      {el}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
          <Field
            type="select"
            name="unit"
            control={control}
            label={t("indicators.Unit")}
            options={selectOptions(INDICATOR_UNITS)}
            labelInValue
            disabled={isRemoved}
          />
          <Field
            type="select"
            name="period"
            control={control}
            label={t("indicators.period")}
            options={selectOptions(INDICATOR_PERIODS)}
            labelInValue
            disabled={isRemoved}
          />
          {currentId && (
            <Field
              type="input"
              name="createdAt"
              control={control}
              label={t("indicators.createdAt")}
              disabled
            />
          )}
        </Flex>
      </form>
      <Modal
        open={openChildModal === "Enum"}
        title={t("indicators.indicator_symbol_title")}
        onCancel={handleCloseChildModal}
        closable
        centered
        destroyOnHidden
        okButton={{
          htmlType: "submit",
          form: enumFormId,
          children: t("ui.save"),
        }}
        cancelButton={{ children: t("ui.close") }}
      >
        <EnumForm
          formId={enumFormId}
          symbols={symbols}
          refetch={refetch}
          setCloseModal={handleCloseChildModal}
          onSet={setValue}
        />
      </Modal>
    </>
  );
};
export default IndicatorForm;
